import type { I18n } from "@lingui/core";
import { useCallback, useDeferredValue, useMemo, useState } from "react";
import type { UserAnalyticsViewPresetID } from "../_utils/preset-config";
import { getPresetInfo } from "../_utils/preset-config";

export interface ViewPreset {
  presetID: UserAnalyticsViewPresetID;
  name: string;
  description?: string;
}

export function usePresetSelection(
  viewPresets: ViewPreset[],
  i18n: I18n,
  defaultSelectAll: boolean = true,
) {
  const [selectedPresetIds, setSelectedPresetIds] = useState<
    UserAnalyticsViewPresetID[]
  >(() => (defaultSelectAll ? viewPresets.map((p) => p.presetID) : [])); // デフォルトの選択状態を制御
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  // React 19のuseDeferredValueでデバウンス効果を実現
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const handlePresetToggle = useCallback(
    (presetId: UserAnalyticsViewPresetID) => {
      setSelectedPresetIds((prev) =>
        prev.includes(presetId)
          ? prev.filter((id) => id !== presetId)
          : [...prev, presetId],
      );
    },
    [],
  );

  const handleCategoryToggle = useCallback((category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedPresetIds.length === viewPresets.length) {
      setSelectedPresetIds([]);
    } else {
      setSelectedPresetIds(viewPresets.map((p) => p.presetID));
    }
  }, [selectedPresetIds.length, viewPresets]);

  const clearSelection = useCallback(() => {
    setSelectedPresetIds([]);
    setSearchQuery("");
  }, []);

  const { categories, filteredPresets } = useMemo(() => {
    // カテゴリー別にグループ化
    const categories: Record<
      string,
      {
        label: string;
        color: string;
        presets: Array<{
          presetID: UserAnalyticsViewPresetID;
          name: string;
          description?: string;
        }>;
      }
    > = {};

    for (const preset of viewPresets) {
      const presetInfo = getPresetInfo(
        preset.presetID as UserAnalyticsViewPresetID,
        i18n,
      );
      if (!presetInfo) continue;

      const { category, categoryLabel, categoryColor } = presetInfo;

      if (!categories[category]) {
        categories[category] = {
          label: categoryLabel,
          color: categoryColor,
          presets: [],
        };
      }

      categories[category].presets.push({
        presetID: preset.presetID,
        name: preset.name,
        description: preset.description,
      });
    }

    // Sort categories and presets
    Object.values(categories).forEach((category) => {
      category.presets.sort((a, b) =>
        a.name.localeCompare(b.name, i18n.locale),
      );
    });

    // 検索フィルタリング（デバウンスされたクエリを使用）
    const searchLower = deferredSearchQuery.toLowerCase();
    const filteredPresets = viewPresets.filter((preset) => {
      if (!deferredSearchQuery) return true;

      const presetInfo = getPresetInfo(
        preset.presetID as UserAnalyticsViewPresetID,
        i18n,
      );

      return (
        preset.name.toLowerCase().includes(searchLower) ||
        preset.description?.toLowerCase().includes(searchLower) ||
        preset.presetID.toLowerCase().includes(searchLower) ||
        presetInfo?.categoryLabel.toLowerCase().includes(searchLower)
      );
    });

    return { categories, filteredPresets };
  }, [viewPresets, deferredSearchQuery, i18n]);

  // フィルタされたプリセットをカテゴリー情報と共に返す
  const filteredPresetsWithCategory = useMemo(() => {
    return filteredPresets.map((preset) => {
      const presetInfo = getPresetInfo(
        preset.presetID as UserAnalyticsViewPresetID,
        i18n,
      );
      return {
        ...preset,
        categoryKey: presetInfo?.category || "",
        categoryLabel: presetInfo?.categoryLabel || "",
        categoryColor: presetInfo?.categoryColor || "",
      };
    });
  }, [filteredPresets, i18n]);

  return {
    selectedPresetIds,
    expandedCategories,
    searchQuery,
    deferredSearchQuery,
    categories,
    filteredPresets: filteredPresetsWithCategory,
    handlePresetToggle,
    handleCategoryToggle,
    handleSelectAll,
    clearSelection,
    setSearchQuery,
  };
}
